/**
 * Company profile and setup, company users and invitations, and the
 * checklist/project templates.
 */

import { request } from './client.js';

// The login flow's company-choice list uses the same interface name; the two
// declarations merged in the original single-file client and merge here too.
export interface Company {
  readonly id: string;
  readonly name: string;
}

// ------------------------------------------------------------- company & people

export interface Company {
  readonly id: string;
  readonly name: string;
  readonly legal_name: string | null;
  readonly trade_licence_no: string | null;
  readonly timezone: string;
  readonly default_currency: string;
  readonly vat_rate: string;
  /** The logo is a company document; render it via `companyLogoUrl()`. */
  readonly logo_document_id: string | null;
}

export function getCompany(): Promise<Company> {
  return request<Company>('/v1/company');
}

/** The logo bytes, inline. 404s until one is set. */
export function companyLogoUrl(): string {
  return '/v1/company/logo';
}

export function uploadCompanyLogo(file: File): Promise<Company> {
  const params = new URLSearchParams({
    filename: file.name,
    mime_type: file.type || 'application/octet-stream',
  });
  return request<Company>(`/v1/company/logo?${params.toString()}`, {
    method: 'POST',
    body: file,
    headers: { 'content-type': 'application/octet-stream' },
  });
}

export function updateCompany(input: {
  name?: string;
  legal_name?: string | null;
  trade_licence_no?: string | null;
  timezone?: string;
  default_currency?: string;
  vat_rate?: string;
}): Promise<Company> {
  return request<Company>('/v1/company', { method: 'PATCH', body: JSON.stringify(input) });
}

export interface SetupStep {
  readonly key: 'profile' | 'team' | 'first_project';
  readonly title: string;
  readonly why: string;
  readonly done: boolean;
  readonly href: string;
}

export interface SetupState {
  readonly steps: readonly SetupStep[];
  readonly remaining: number;
  readonly complete: boolean;
  readonly dismissed: boolean;
}

export function getSetup(): Promise<SetupState> {
  return request<SetupState>('/v1/company/setup');
}

export function setSetupDismissed(dismissed: boolean): Promise<SetupState> {
  return request<SetupState>('/v1/company/setup', {
    method: 'PATCH',
    body: JSON.stringify({ dismissed }),
  });
}

export interface CompanyUser {
  readonly user_id: string;
  readonly name: string;
  readonly email: string;
  readonly company_role: string;
  readonly status: string;
  readonly last_login_at: string | null;
  readonly project_count: number;
}

export async function listCompanyUsers(): Promise<readonly CompanyUser[]> {
  return (await request<{ data: readonly CompanyUser[] }>('/v1/users')).data;
}

export async function updateCompanyUser(
  userId: string,
  input: { company_role?: string; status?: 'active' | 'suspended' },
): Promise<readonly CompanyUser[]> {
  const body = await request<{ data: readonly CompanyUser[] }>(
    `/v1/users/${encodeURIComponent(userId)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
  );
  return body.data;
}

export interface PendingInvitation {
  readonly id: string;
  readonly email: string;
  readonly company_role: string;
  readonly invited_by: string | null;
  readonly created_at: string;
  readonly expires_at: string;
  readonly expired: boolean;
}

export async function listInvitations(): Promise<readonly PendingInvitation[]> {
  return (await request<{ data: readonly PendingInvitation[] }>('/v1/users/invitations')).data;
}

export interface InvitationIssued {
  readonly invitation_id: string;
  readonly email: string;
  readonly expires_at: string;
  /** The link, returned ONCE. Only its hash is stored. */
  readonly path: string;
}

export function inviteUser(input: {
  email: string;
  company_role: string;
}): Promise<InvitationIssued> {
  return request<InvitationIssued>('/v1/users/invitations', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface ResetIssued {
  readonly reset_id: string;
  readonly email: string;
  readonly name: string;
  readonly expires_at: string;
  /** The link, returned ONCE. Only its hash is stored. */
  readonly path: string;
}

/**
 * Issue a password reset link for a member.
 *
 * There is no mail server, by decision, so nothing is sent: the link comes back
 * here and the admin hands it over themselves.
 */
export function issuePasswordReset(userId: string): Promise<ResetIssued> {
  return request<ResetIssued>(`/v1/users/${encodeURIComponent(userId)}/password-reset`, {
    method: 'POST',
  });
}

export async function revokeInvitation(id: string): Promise<readonly PendingInvitation[]> {
  const body = await request<{ data: readonly PendingInvitation[] }>(
    `/v1/users/invitations/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
  return body.data;
}

// ---------------------------------------------------------------------------
// Templates (J1 step 3, J2 "choose template")
// ---------------------------------------------------------------------------

export interface ChecklistTemplate {
  readonly id: string;
  readonly name: string;
  readonly discipline: string;
  readonly is_active: boolean;
  readonly items: readonly { readonly sequence: number; readonly text: string }[];
}

export interface ProjectTemplate {
  readonly id: string;
  readonly name: string;
  readonly project_type: string;
  readonly is_active: boolean;
  readonly milestones: readonly {
    readonly name: string;
    readonly offset_days: number;
    readonly is_critical: boolean;
    readonly sequence: number;
  }[];
}

export interface TemplatesView {
  readonly checklist: readonly ChecklistTemplate[];
  readonly project: readonly ProjectTemplate[];
}

export function listTemplates(): Promise<TemplatesView> {
  return request<TemplatesView>('/v1/templates');
}


export function createChecklistTemplate(input: {
  name: string;
  discipline?: string;
  items: readonly string[];
}): Promise<TemplatesView> {
  return request<TemplatesView>('/v1/templates/checklist', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateChecklistTemplate(
  templateId: string,
  input: { name?: string; discipline?: string; is_active?: boolean; items?: readonly string[] },
): Promise<TemplatesView> {
  return request<TemplatesView>(`/v1/templates/checklist/${encodeURIComponent(templateId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteChecklistTemplate(templateId: string): Promise<TemplatesView> {
  return request<TemplatesView>(`/v1/templates/checklist/${encodeURIComponent(templateId)}`, {
    method: 'DELETE',
  });
}

export interface TemplateMilestoneInput {
  readonly name: string;
  readonly offset_days: number;
  readonly is_critical?: boolean;
}

export function createProjectTemplate(input: {
  name: string;
  project_type?: string;
  milestones: readonly TemplateMilestoneInput[];
}): Promise<TemplatesView> {
  return request<TemplatesView>('/v1/templates/project', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateProjectTemplate(
  templateId: string,
  input: {
    name?: string;
    project_type?: string;
    is_active?: boolean;
    milestones?: readonly TemplateMilestoneInput[];
  },
): Promise<TemplatesView> {
  return request<TemplatesView>(`/v1/templates/project/${encodeURIComponent(templateId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteProjectTemplate(templateId: string): Promise<TemplatesView> {
  return request<TemplatesView>(`/v1/templates/project/${encodeURIComponent(templateId)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Roles — what each one is allowed to do (ADR-035)
// ---------------------------------------------------------------------------

/** `editable` · `locked` (always on) · `forbidden` (never on). */
export type CellState = 'editable' | 'locked' | 'forbidden';

export interface RoleCell {
  readonly action: string;
  readonly held: boolean;
  readonly scope: string | null;
  readonly requires_finance: boolean;
  readonly state: CellState;
  /** Differs from the shipped default. Drives the "changed" mark. */
  readonly changed: boolean;
}

export interface RoleResourceRow {
  readonly resource: string;
  readonly cells: readonly RoleCell[];
}

export interface RoleView {
  readonly key: string;
  readonly name: string;
  readonly is_customised: boolean;
  readonly customised_at: string | null;
  readonly customised_by: string | null;
  readonly resources: readonly RoleResourceRow[];
}

export async function listRoles(): Promise<readonly RoleView[]> {
  return (await request<{ data: readonly RoleView[] }>('/v1/roles')).data;
}

/**
 * Replace ONE role's permissions. The whole role goes at once — see the note
 * on the route — so `permissions` is the complete list of what stays ticked.
 */
export async function setRolePermissions(
  roleKey: string,
  permissions: readonly { resource: string; actions: readonly string[] }[],
): Promise<readonly RoleView[]> {
  const body = await request<{ data: readonly RoleView[] }>(
    `/v1/roles/${encodeURIComponent(roleKey)}/permissions`,
    { method: 'PATCH', body: JSON.stringify({ permissions }) },
  );
  return body.data;
}

export async function resetRolePermissions(roleKey: string): Promise<readonly RoleView[]> {
  const body = await request<{ data: readonly RoleView[] }>(
    `/v1/roles/${encodeURIComponent(roleKey)}/permissions`,
    { method: 'DELETE' },
  );
  return body.data;
}

// ---------------------------------------------------------------------------
// Thresholds — the numbers the rules judge against (`06` §Configuration)

export interface ThresholdField {
  readonly key: string;
  readonly label: string;
  readonly help: string;
  readonly group: string;
  readonly min: number;
  readonly max: number;
}

export interface ThresholdsView {
  /** What the rules use today. */
  readonly values: Readonly<Record<string, number>>;
  /** What the product recommends. Shown beside every field, per `06`. */
  readonly defaults: Readonly<Record<string, number>>;
  /** The catalogue, so the screen never hard-codes a label or a bound. */
  readonly fields: readonly ThresholdField[];
  readonly changed: readonly string[];
  readonly updated_at: string | null;
  readonly updated_by: string | null;
}

export async function getThresholds(): Promise<ThresholdsView> {
  return (await request<{ data: ThresholdsView }>('/v1/company/thresholds')).data;
}

/** Send ONLY what changed — the server merges. See the note on the route. */
export async function setThresholds(
  thresholds: Readonly<Record<string, number>>,
): Promise<ThresholdsView> {
  const body = await request<{ data: ThresholdsView }>('/v1/company/thresholds', {
    method: 'PATCH',
    body: JSON.stringify({ thresholds }),
  });
  return body.data;
}

export async function resetThresholds(): Promise<ThresholdsView> {
  const body = await request<{ data: ThresholdsView }>('/v1/company/thresholds', {
    method: 'DELETE',
  });
  return body.data;
}
